import { useEffect, useRef, useState } from 'react';
import { useViewport } from '@xyflow/react';

/**
 * Animated, futuristic background for the workflow canvas:
 *   1. Perspective grid (CSS 3D)
 *   2. Reactive aurora glow that follows the mouse
 *   3. Drifting gold particles
 *   4. Parallax that responds to React Flow pan/zoom
 *
 * Respects prefers-reduced-motion: drops animations and parallax.
 */
const PARTICLE_COUNT = 28;

export function CanvasBackground() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });
  const viewport = useViewport();
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Mouse tracking, throttled via rAF
  useEffect(() => {
    if (reduced) return;
    let frame = 0;
    let pending = { x: 0.5, y: 0.5 };
    function onMove(e: MouseEvent) {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      pending = {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      };
      if (!frame) {
        frame = requestAnimationFrame(() => {
          setMouse(pending);
          frame = 0;
        });
      }
    }
    const wrapper = containerRef.current?.parentElement;
    if (wrapper) {
      wrapper.addEventListener('mousemove', onMove);
      return () => {
        wrapper.removeEventListener('mousemove', onMove);
        if (frame) cancelAnimationFrame(frame);
      };
    }
  }, [reduced]);

  // Parallax offset: gentle response to viewport pan/zoom
  const parallaxX = reduced ? 0 : viewport.x * 0.08;
  const parallaxY = reduced ? 0 : viewport.y * 0.08;

  // Particles generated once
  const particlesRef = useRef<Array<{ x: number; y: number; size: number; delay: number; duration: number }>>([]);
  if (particlesRef.current.length === 0) {
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particlesRef.current.push({
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: 1 + Math.random() * 2.5,
        delay: Math.random() * 10,
        duration: 16 + Math.random() * 18,
      });
    }
  }

  return (
    <div
      ref={containerRef}
      className={`canvas-bg ${reduced ? 'reduced' : ''}`}
      style={{
        '--mx': mouse.x,
        '--my': mouse.y,
        '--px': `${parallaxX}px`,
        '--py': `${parallaxY}px`,
      } as React.CSSProperties}
      aria-hidden="true"
    >
      <div className="bg-deep" />
      <div className="bg-grid-perspective">
        <div className="bg-grid-perspective-inner" />
      </div>
      <div className="bg-grid-flat" />
      <div className="bg-aurora" />
      <div className="bg-scan" />
      {!reduced && (
        <div className="bg-particles">
          {particlesRef.current.map((p, i) => (
            <span
              key={i}
              className="particle"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                animationDelay: `${p.delay}s`,
                animationDuration: `${p.duration}s`,
              }}
            />
          ))}
        </div>
      )}
      <div className="bg-vignette" />
    </div>
  );
}
